import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";
import WebsiteMediaInput from "./WebsiteMediaInput";

const HOME_SECTIONS = {
    hero: {
        title: "Top banner",
        highlights: ["This is the first block on the home page"],
        fields: [
            { field: "hero_badge", label: "Small line above the heading", hint: "Short orange text at the top of the banner." },
            { field: "hero_badge_secondary", label: "Second short line", hint: "Shown after a dot, next to that orange line." },
            { field: "hero_title", label: "Main heading", hint: "The large heading. Start a new line to break the heading on the website.", type: "textarea" },
            { field: "hero_highlighted_text", label: "Underlined words", hint: "Shown under the main heading, with an orange underline." },
            { field: "hero_description", label: "Paragraph under the heading", type: "textarea" },
            { field: "hero_image", label: "Banner photo", type: "image", hint: "Required size: 1600 � 1000 px (16:10). A larger photo is scaled to fit this banner on phone, tablet, and laptop. The whole photo stays visible." },
            { field: "hero_image_alt", label: "Banner photo description", hint: "Read by screen readers. Describe what is in the photo." },
            { field: "hero_primary_text", label: "Main button text", hint: "The light button under the paragraph." },
            { field: "hero_primary_url", label: "Main button link", hint: "Page that button opens, such as /courses." },
            { field: "hero_secondary_url", label: "Download app link", hint: "The app download button. Leave blank to hide that button." },
            { field: "hero_float_top", label: "Label on the top of the photo", hint: "Small chip at the top-left of the banner photo." },
            { field: "hero_float_bottom", label: "Label on the bottom of the photo", hint: "Small chip at the bottom-right of the banner photo." },
        ],
    },
    stats: {
        title: "Number cards",
        highlights: ["The dark row of big numbers under the banner"],
        listTitle: "Numbers in this row",
        fields: [
            { field: "stats_eyebrow", label: "Small line above the heading", hint: "Example: Proven Excellence." },
            { field: "stats_title", label: "Heading above the numbers" },
        ],
    },
    about: {
        title: "About story",
        highlights: ["The story block with a photo and short highlight rows"],
        listTitle: "Highlight rows under the story",
        fields: [
            { field: "about_eyebrow", label: "Small line above the heading", hint: "Example: The SAHAS Story." },
            { field: "about_title", label: "Heading" },
            { field: "about_description", label: "Story text", hint: "Each new line becomes its own paragraph on the website.", type: "textarea" },
            { field: "about_image", label: "Story photo", type: "image", hint: "Required size: 880 � 605 px (16:11). A larger photo is scaled to fit this frame. The whole photo stays visible." },
            { field: "about_image_alt", label: "Story photo description", hint: "Read by screen readers. Describe what is in the photo." },
            { field: "about_cta_text", label: "Link text under the story", hint: "Example: Read our story. Leave blank to hide the link." },
            { field: "about_cta_url", label: "Where that link goes", hint: "Example: /about-us." },
        ],
    },
    founder: {
        title: "Founder",
        highlights: ["The founder block on the home page"],
        fields: [
            { field: "founder_name", label: "Founder name", hint: "Shown as the heading of this block." },
            { field: "founder_designation", label: "Role under the name", hint: "Example: Founder, SAHAS Institute of Commerce." },
            { field: "founder_image", label: "Founder photo", type: "image", hint: "Required size: 560 � 560 px (square). A larger photo is scaled to fit this frame. The whole photo stays visible." },
            { field: "founder_image_alt", label: "Photo description", hint: "Read by screen readers. Use the founder's name." },
            { field: "founder_message", label: "First paragraph", type: "textarea" },
            { field: "founder_description", label: "Second paragraph", type: "textarea" },
            { field: "founder_cta_text", label: "Button text", hint: "Leave blank to hide the button." },
            { field: "founder_cta_url", label: "Where the button goes" },
        ],
    },
    facilities: {
        title: "Facilities heading",
        highlights: ["Only the words above the facility cards on the home page"],
        note: "The facility cards themselves, including their photos, are edited on the Facilities page. The home page shows the first 8.",
        link: "/manage-website/pages/facilities",
        linkLabel: "Open Facilities page",
        fields: [
            { field: "facilities_eyebrow", label: "Small line above the heading" },
            { field: "facilities_title", label: "Heading", hint: "Example: Our Facilities." },
            { field: "facilities_description", label: "Line under the heading", type: "textarea" },
            { field: "facilities_view_all_text", label: "Button text", hint: "Example: Explore All Facilities. Leave blank to hide the button." },
            { field: "facilities_view_all_url", label: "Where the button goes", hint: "Usually /facilities." },
        ],
    },
    courses: {
        title: "Courses heading",
        highlights: ["Only the words above the course cards on the home page"],
        note: "Course names, fees, and photos are shared by the whole website. Edit those under Courses. This screen changes the heading on the home page only.",
        link: "/manage-website/pages/courses",
        linkLabel: "Open Courses",
        fields: [
            { field: "courses_eyebrow", label: "Small line above the heading" },
            { field: "courses_title", label: "Heading", hint: "Example: Courses We Offer." },
            { field: "courses_description", label: "Line under the heading", type: "textarea" },
            { field: "courses_view_all_text", label: "Button text", hint: "Example: View All Courses. Leave blank to hide the button." },
            { field: "courses_view_all_url", label: "Where the button goes", hint: "Usually /courses." },
        ],
    },
    app: {
        title: "App block",
        highlights: ["The dark app block on the home page"],
        listTitle: "Checklist under the app text",
        fields: [
            { field: "app_eyebrow", label: "Small line above the heading" },
            { field: "app_title", label: "Heading" },
            { field: "app_description", label: "Paragraph under the heading", type: "textarea" },
            { field: "app_image", label: "Phone screenshot", type: "image", hint: "Required size: 640 � 1280 px (1:2). A larger screenshot is scaled to fit this phone frame. The whole screenshot stays visible." },
            { field: "app_image_alt", label: "Screenshot description", hint: "Read by screen readers." },
            { field: "app_android_url", label: "Android download link", hint: "Leave blank to hide the Android button." },
            { field: "app_ios_url", label: "iPhone download link", hint: "Leave blank to hide the iPhone button." },
            { field: "app_store_label", label: "iPhone button text", hint: "Example: Download on App Store." },
        ],
    },
    mission: {
        title: "Mission and vision",
        highlights: ["The two cards under the app block"],
        fields: [
            { field: "mission_title", label: "Mission card title", hint: "Shown on the dark card. Example: Our Mission." },
            { field: "mission_text", label: "Mission text", type: "textarea" },
            { field: "vision_title", label: "Vision card title", hint: "Shown on the light card. Example: Our Vision." },
            { field: "vision_text", label: "Vision text", type: "textarea" },
        ],
    },
    testimonials: {
        title: "Testimonials",
        highlights: ["These words appear above the review slider on the home page"],
        fields: [
            { field: "testimonials_eyebrow", label: "Small line above the heading", hint: "Example: Student & Parent Feedback." },
            { field: "testimonials_title", label: "Heading", hint: "The large heading above the slider." },
            { field: "testimonials_description", label: "Line under the heading", type: "textarea" },
            { field: "testimonials_trust_label", label: "Trust line", hint: "A short line under the description, such as a student count. Leave empty to hide it." },
        ],
    },
    achievements: {
        title: "Achievements",
        highlights: ["Hall of fame cards. The heading on the website stays SAHAS Achievements"],
        intro: "The heading on the website stays �SAHAS Achievements�. Add, edit, or remove the cards below.",
        listTitle: "Achievement cards",
    },
    gallery: {
        title: "Gallery preview",
        highlights: ["Scrolling photo cards on the home page"],
        intro: "The heading on the website stays �Gallery Preview�. Required size for each photo: 720 � 450 px (16:10). A larger photo is scaled to fit the card. The whole photo stays visible.",
        listTitle: "Photo cards",
    },
    brochures: {
        title: "Brochure cards",
        highlights: ["The download cards on the home page"],
        intro: "The heading on the website stays �Download e-Brochures�. These cards are the home page teasers. The full brochure library is edited on the e-Brochures page.",
        listTitle: "Cards on the home page",
    },
    free: {
        title: "Free app content",
        highlights: ["The free-content cards near the bottom of the home page"],
        fields: [
            { field: "free_title", label: "Heading", hint: "Example: Free App Content." },
            { field: "free_description", label: "Line under the heading", type: "textarea" },
            {
                field: "free_items",
                label: "Cards",
                type: "textarea",
                hint: "One card per line, written as icon|title|description. Example: menu_book|Notes|Chapter notes inside the app.",
            },
        ],
    },
    contact: {
        title: "Follow us",
        highlights: ["The social block at the bottom of the home page"],
        listTitle: "Social buttons",
        fields: [
            { field: "follow_title", label: "Heading", hint: "Example: Follow SAHAS." },
            { field: "follow_description", label: "Line under the heading", type: "textarea" },
        ],
    },
};

function WebsiteHomeFields({ section }) {
    const { requestAPI, showToast } = useAppContext();
    const navigate = useNavigate();
    const config = HOME_SECTIONS[section];
    const [loading, setLoading] = useState();
    const [saving, setSaving] = useState();
    const [error, setError] = useState();
    const [record, setRecord] = useState();

    useEffect(() => {
        requestAPI({
            requestPath: "website/home",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRecord(data);
                else setError("Couldn't load Home");
            },
        });
    }, [requestAPI]);

    const saveRecord = () => {
        requestAPI({
            requestPath: "website/home",
            requestMethod: "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Home !", life: 2000 }),
            onResponseReceieved: (updated, responseCode) => {
                if (updated && responseCode === 200) {
                    setRecord(updated);
                    showToast({ severity: "success", summary: "Updated", detail: "Home Updated", life: 1000 });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: updated?.error || "Failed To Update Home !", life: 2000 });
                }
            },
        });
    };

    if (loading) return <Loading />;
    if (error) return <NoContent error={error} />;
    if (!record) return null;

    return (
        <div className="p-3 flex flex-column">
            {config.note ? <p className={`${TEXT_SMALL} text-color-secondary m-0 mb-2`}>{config.note}</p> : null}
            {config.fields.map((item) => (
                <HomeField key={item.field} item={item} record={record} setRecord={setRecord} disabled={saving} />
            ))}
            <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={saveRecord} />
            {config.link ? (
                <Button className="mt-3 align-self-start" label={config.linkLabel} icon="pi pi-arrow-right" iconPos="right" severity="secondary" onClick={() => navigate(config.link)} />
            ) : null}
        </div>
    );
}

function HomeField({ item, record, setRecord, disabled }) {
    const value = record?.[item.field] ?? "";
    const setValue = (next) => setRecord((prev) => ({ ...prev, [item.field]: next }));

    if (item.type === "image") {
        return <WebsiteMediaInput label={item.label} field={item.field} hint={item.hint} record={record} setRecord={setRecord} disabled={disabled} />;
    }

    return (
        <div>
            <FloatLabel className="mt-5">
                {item.type === "textarea" ? (
                    <InputTextarea id={item.field} className="w-full" rows={4} autoResize value={value || ""} onChange={(event) => setValue(event.target.value)} disabled={disabled} />
                ) : (
                    <InputText id={item.field} className="w-full" value={value} onChange={(event) => setValue(event.target.value)} disabled={disabled} pt={{ root: { className: TEXT_NORMAL } }} />
                )}
                <label htmlFor={item.field}>{item.label}</label>
            </FloatLabel>
            <FieldNote text={item.hint} />
        </div>
    );
}

export function HomeBlock({ section, List }) {
    const config = HOME_SECTIONS[section];

    if (!config) return null;

    return (
        <div className="flex flex-column h-full">
            <PageTitle title={config.title} />
            <TabHeader
                className="px-3 pt-3"
                title={config.title}
                highlights={config.highlights}
            />
            <Divider />
            <div className="flex-1 overflow-y-scroll">
                {config.intro ? <p className={`${TEXT_SMALL} text-color-secondary px-3 pt-3 m-0`}>{config.intro}</p> : null}
                {config.fields?.length ? <WebsiteHomeFields section={section} /> : null}
                {List ? <List bare title={config.listTitle} /> : null}
                </div>
        </div>
    );
}
